import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  FormCaption,
  FormContainer,
  InformSpan,
  SixDigitInput
} from '../../common/components';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { APP_KEYS } from '../../common/consts';
import { authService } from '../service/auth.service';
import { IActivateAccount } from '../types';
import { useThrottle } from '../../hooks/useThrottle';
import { ErrorIcon } from '../../common/components/icons/error-icon.component';
import { useCodeRegister } from '../../hooks/codeRegister';
import useAuth from '../feature/auth';

interface Inputs {
  code: string;
}

export const ActivateAccount = () => {
  const { t } = useTranslation();
  const [serverError, setServerError] = React.useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated, checkAuth } = useAuth();
  const { email, trial } =
    (location.state as { email?: string; trial?: string }) ?? {};

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<Inputs>();

  const { mutate: activate } = useMutation({
    mutationFn: (data: IActivateAccount) => {
      return authService.activateAccount(data, trial);
    },
    onSuccess: async () => {
      setIsAuthenticated(true);
      await checkAuth();
      navigate(trial ? ROUTER_LINKS.MANAGEMENT : ROUTER_LINKS.ROOT);
    },

    onError: (err: any) => {
      const serverMessage = err?.response?.data?.message;
      if (serverMessage === 'Invalid token') {
        setServerError(t('auth.codeIncorrect'));
      } else {
        setServerError(serverMessage || t('auth.activateFailed'));
      }
    }
  });
  const throttledActivate = useThrottle(activate);

  const { mutate: resendCode } = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.RECOVERY_CODE],
    mutationFn: (email: string) => {
      return authService.resendActivationCode({ email });
    },
    onSuccess: () => {
      setServerError('');
    },
    onError: () => {
      setServerError(t('auth.resendFailed'));
    }
  });
  const throttledResendCode = useThrottle(resendCode);

  const positionStyles = errors.code ? 'bottom-14' : 'bottom-5';

  const {
    codeRegister,
    resendCodeHandler,
    onSubmit,
    formattedValue,
    setFormattedValue
  } = useCodeRegister({
    register,
    throttledResendCode,
    throttledSubmit: throttledActivate,
    handleSubmit,
    setValue,
    email: email ?? undefined
  });

  return (
    <div className={`relative ${serverError ? 'pt-[22px] md:pt-[32px]' : ''}`}>
      {serverError && (
        <div className="absolute flex justify-center gap-[8px] items-center top-[-22px] md:top-[-32px] left-[-22px] md:left-[-32px] w-[345px] md:w-[456px] h-[32px] bg-red-500 text-surface-base text-center rounded-t-[42px] py-[6px] text-surface-base font-body text-[14px] font-regular">
          <ErrorIcon />
          {serverError}
        </div>
      )}
      <FormCaption>{t('auth.activateTitle')}</FormCaption>

      <InformSpan>
        {t('auth.activateCodeSent')} {email ?? 'name@company.com'}
        <Link
          to={ROUTER_LINKS.SIGNIN}
          className="text-base-black ml-[10px] font-medium hover:text-blue-500"
        >
          {t('auth.change')}
        </Link>
      </InformSpan>

      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <SixDigitInput
          errors={errors}
          name="code"
          setFormattedValue={setFormattedValue}
          formattedValue={formattedValue}
          register={codeRegister}
        >
          <div className="flex gap-[10px] justify-center text-center mt-[24px]">
            <p className="text-[16px] font-regular font-body text-gray-900">
              {t('auth.dontSeeCode')}
            </p>
            <div
              className={`flex right-4 ${positionStyles} text-blue-500 hover:text-blue-700 cursor-pointer`}
              onClick={resendCodeHandler}
            >
              <span className="text-[16px] font-medium font-body text-base-black">
                {t('auth.resendCode')}
              </span>
            </div>
          </div>
        </SixDigitInput>
      </FormContainer>
    </div>
  );
};
